<template>
  <section class="home-slider">
    <AppEyelet
      :data="{ content: dataPage.eyelet }"
    />
    <AppLine :isSmall="true" />
    <AppTitle
      :data="{ content: dataPage.title }"
      class="c-title--dark"
    />
    <AppLine :isSmall="true" />
    <transition @enter="enterTitle">
      <AppTitle
        v-if="loaded"
        type="tube"
        :autoPlay="false"
        :data="{ content: titles }"
        class="c-title--light"
        ref="speakerTitle"
      />
    </transition>
    <AppLine :isSmall="true" />

    <div class="home-slider__container"
      @mouseenter="$eventHub.$emit('morphCursor', 'drag')"
      @mouseleave="$eventHub.$emit('morphCursor', 'rect')"
      data-lazyanimation="stagger"
    >
      <transition name="fade">
        <appEyelet
          :data="{ content: parsedSlide(data[currentIndex]).eyelet }"
          :key="currentIndex"
          :isAnimated="false"
          v-if="data.length > 0"
        />
      </transition>
      <div
        class="slider"
        :style="{
          width: `${(unit * 11) * data.length + (spaceSlides) * data.length}px`,
        }"
        ref="slider"
      >
        <div
          class="slider__item"
          v-for="(item, index) in data"
          :key="index"
          data-item
        >
          <router-link :to="parsedSlide(item).url"
            class="slider__item-cont"
            :style="{
              transform: `scale(${sliderItems.length > 0 ? sliderItems[index].scale : 1})`,
            }"
            aria-label="Speaker link"
          >
            <AppPicture
              class="slider__item-image"
              @mouseenter.native="mouseEnterPicture(index)"
              @mouseleave.native="$eventHub.$emit('morphCursor', 'drag')"
              :data="{ src: parsedSlide(item).pictureUrl, alt: parsedSlide(item).alt }"
              :lazy="false"
            />
          </router-link>
        </div>
      </div>
      <div class="slider__mobile-button u-hidden--from-ipadL" @click="slideNext" />
      <div class="slider__cursor-mobile" data-animation="scale">
        <svg
          class="pointer__drag"
          x="0px" y="0px"
          viewBox="0 0 55 45"
        >
          <polygon points="54,22.5 48,28.5 48,16.5 "/>
          <path d="M27.5,1V44"/>
          <polygon points="1,22.5 7,28.5 7,16.5 "/>
        </svg>
      </div>
    </div>

    <div class="slider__button-cont">
      <AppButton
        :router="true"
        :data="{
          content: dataPage.button,
          url: `/${$route.params.city}/${type}`,
        }"
      />
    </div>
  </section>
</template>

<script>
import Hammer from 'hammerjs';
import anime from 'animejs';

import AppLine from '@/components/AppLine.vue';
import AppTitle from '@/components/AppTitle.vue';
import AppEyelet from '@/components/AppEyelet.vue';
import AppPicture from '@/components/AppPicture.vue';
import AppButton from '@/components/AppButton.vue';

import medusa from '@/scripts/medusa';
import { isTouchDevice } from '@/scripts/utils';
import { initials } from '@/scripts/fx';

import slider from '@/assets/data/contents/slider.json';

export default {
  name: 'HomeSlider',

  components: {
    AppLine,
    AppTitle,
    AppEyelet,
    AppPicture,
    AppButton,
  },

  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    type: {
      type: String,
      required: true,
      default: () => 'speakers',
    },
  },

  data() {
    return {
      slider,
      loaded: false,
      parsed: [{}, {}, {}],
      hm: null,
      partialAmount: 0,
      amount: 0,
      slide: 0,
      unit: 0,
      isSwiping: false,
      sliderItems: [],
      titles: [],
      spaceSlides: 0,
      translateSlider: 0,
      slidePerView: 0,
      currentIndex: 0,
    };
  },

  computed: {
    dataPage() {
      return this.slider[this.$route.params.city][this.type];
    },
  },

  watch: {
    data(newVal) {
      if (newVal.length > 0) {
        this.initializeSlider(newVal);
      }
    },
  },

  mounted() {
    if (this.data.length > 0) this.initializeSlider(this.data);
    this.hm = new Hammer(this.$el);
    this.hm.on('swipe', (event) => {
      if (this.isSwiping) return;
      this.isSwiping = true;
      this.slide += event.deltaX > 0 ? -1 : 1;
      this.slide = Math.min(Math.max(this.slide, 0), (this.data.length / this.slidePerView) - 1);

      anime({
        targets: this.$refs.slider,
        translateX: -1 * this.slide * this.translateSlider,
        easing: 'spring(4, 100, 30, 2)',
        update: () => {
          this.sliderItems.forEach(el => el.calcScale());
        },
        begin: () => {
          if (isTouchDevice() && this.$refs.speakerTitle !== undefined) {
            this.currentIndex = this.slide;
            this.$refs.speakerTitle.goTo(this.slide);
          }
        },
      }).finished.then(() => {
        this.isSwiping = false;
      });
    });

    this.resize();
    this.$eventHub.$on('windowResize', this.resize);
  },

  methods: {
    resize() {
      switch (true) {
        case window.innerWidth < 768:
          this.unit = window.innerWidth / 22;
          this.spaceSlides = this.unit * 6.5;
          this.slidePerView = 1;
          this.translateSlider = this.unit * 17.82;
          break;

        case window.innerWidth < 1280:
          this.unit = window.innerWidth / 44;
          this.spaceSlides = this.unit * 4;
          this.slidePerView = 1;
          this.translateSlider = this.unit * 15.42;
          break;

        default:
          this.unit = window.innerWidth / 64;
          this.spaceSlides = this.unit * 4;
          this.slidePerView = 3;
          this.translateSlider = this.unit * 16 * 3;
          break;
      }

      anime({
        targets: this.$refs.slider,
        translateX: 0,
        easing: 'spring(4, 100, 30, 2)',
        update: () => {
          this.sliderItems.forEach(el => el.calcScale());
        },
        begin: () => {
          this.slide = 0;
          if (isTouchDevice() && this.$refs.speakerTitle !== undefined) {
            this.$refs.speakerTitle.goTo(this.slide);
          }
        },
      }).finished.then(() => {
        this.isSwiping = false;
      });
    },
    initializeSlider(data) {
      data.forEach((slide) => {
        const title = slide.name !== undefined ? slide.name : slide.hostName;
        this.titles.push(title);
      });

      this.$nextTick(() => {
        Array.from(this.$el.querySelectorAll('.slider__item')).forEach(el => this.sliderItems.push(this.createSlidesObj(el)));
        this.sliderItems.forEach(el => el.calcScale());
        this.enterSlides();
      });
      this.loaded = true;
    },
    mouseEnterPicture(index) {
      if (index === undefined) return;
      if (this.$refs.speakerTitle === undefined) return;

      this.currentIndex = index;
      if (!isTouchDevice()) this.$refs.speakerTitle.goTo(index);
      this.$eventHub.$emit('morphCursor', 'circle');
    },
    slideNext() {
      this.isSwiping = true;
      this.slide += 1;
      this.slide = Math.min(Math.max(this.slide, 0), (this.data.length / this.slidePerView) - 1);

      anime({
        targets: this.$refs.slider,
        translateX: -1 * this.slide * this.translateSlider,
        easing: 'spring(4, 100, 30, 2)',
        update: () => {
          this.sliderItems.forEach(el => el.calcScale());
        },
        begin: () => {
          if (isTouchDevice() && this.$refs.speakerTitle !== undefined) {
            this.currentIndex = this.slide;
            this.$refs.speakerTitle.goTo(this.slide);
          }
        },
      }).finished.then(() => {
        this.isSwiping = false;
      });
    },
    createSlidesObj(el) {
      return {
        el,
        bounding() {
          return this.el.getBoundingClientRect();
        },
        left() {
          return this.bounding().left;
        },
        centerPosition() {
          return this.left() + (this.bounding().width / 2);
        },
        scale: 1,
        calcScale() {
          const center = window.innerWidth * window.innerWidth < 768 ? 0.4375 : window.innerWidth < 1024 ? 0.2656 : 0.4578; // eslint-disable-line
          this.scale = 1 + Math.abs((Math.abs((this.centerPosition() - center)
            / (window.innerWidth - center)) - 1) / 2);

          return this.scale;
        },
      };
    },
    parsedSlide(slide) {
      return {
        pictureUrl: this.type === 'speakers' ? slide.pictureUrl : slide.hostPicture,
        alt: this.type === 'speakers' ? slide.name : slide.hostName,
        eyelet: this.type === 'speakers' ? slide.subtitle : slide.workshopTitle,
        url: `/${this.$route.params.city}/${this.type}/${slide.path}`,
      };
    },
    enterSlides() {
      const itemsContainer = this.$el.querySelector('[data-lazyanimation]');
      const items = [...this.$el.querySelectorAll('[data-item]')];
      items.forEach(el => initials.get('fade')(el, 150));
      medusa.ref.pushToTarget('scrollAnimations', itemsContainer);
      medusa.prevNodes.push(itemsContainer);
    },
    enterTitle(el, done) {
      const elToAnimate = el.querySelector('[data-animation]');

      medusa.ref.pushToTarget('scrollAnimations', elToAnimate);
      medusa.prevNodes.push(elToAnimate);
      initials.get(elToAnimate.dataset.animation)(elToAnimate);

      done();
    },
  },
};
</script>

<style lang="scss" scoped>
.home-slider {
  &__container {
    position: relative;
  }

  .slider {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    margin: $unit-vh * 3 0px;

    @include mq(ipadP) {
      margin: $unit-vh * 2 0px;
    }

    &__item {
      position: relative;
      width: $unit-vw * 48;

      @include aspect-ratio(11, 14);

      overflow: hidden;

      @include mq(ipadP) {
        width: $unit-vw * 20;

        &:nth-child(even) {
          margin-top: $unit-vh * 4;
        }
      }

      @include mq(ipadL) {
        width: $unit-vw * 18;
      }

      @include mq(ds) {
        width: $unit-vw * 11;
      }
    }

    &__item-cont,
    &__item-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    &__button-cont {
      position: relative;
      width: 100%;

      @include mq($until: ipadP) {
        text-align: center;
        margin-left: 0 !important;
      }

      @include mq($until: ipadL, $and: $is-touch-device) {
        margin-left: 20px;
      }
    }

    &__cursor-mobile {
      display: none;

      @include mq($and: $is-touch-device) {
        display: block;
        position: absolute;
        top: 50%;
        right: 0;
        width: 80px;
        height: 80px;
        margin-top: -40px;

        background-color: var(--color-headline-dark);
        border-radius: 50%;

        svg {
          position: absolute;
          width: 40px;
          height: 40px;
          top: 50%;
          left: 50%;

          transform: translateX(-20px) translateY(-20px);
        }

        polygon,
        path {
          fill: none;
          stroke: var(--color-headline-light);
          stroke-width: 1.2;
        }

        @include mq(ipadP) {
          width: 100px;
          height: 100px;
          margin-top: -50px;

          svg {
            width: 50px;
            height: 50px;

            transform: translateX(-25px) translateY(-25px);
          }
        }
      }
    }
  }

  .slider__mobile-button {
    position: absolute;
    bottom: 0;
    right: -20px;
    width: $unit-vw * 10;
    height: 100%;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s $outCirc,
      transform .5s $outCirc;
  }

  .fade-enter, .fade-leave-to {
    position: absolute;
    opacity: 0;
    transform: translateY(20px);
  }

  .fade-leave-to {
    transform: translateY(-20px);
  }
}
</style>
<style lang="scss">
.home-slider {
  @include mq($and: $is-not-touch-device) {
    & a .picture__img {
      opacity: 0.7;
      transition: transform .4s $outCirc,
        opacity .4s $outBack;
    }
    & a:hover .picture__img {
      opacity: 1;
      transform: scale(1.1);
      transition: transform .4s $outBack,
        opacity .4s $outBack
    }
  }
}
</style>
